// Écran Bienvenue Premium — animation post-activation.
// Affiché une seule fois après l'achat (flag persistant en localStorage).
// Met en avant les contenus débloqués : séances, ambiances, univers bébé.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { store } from "@/lib/storage";
import { SESSIONS, AMBIENTS, BABY_AMBIENTS } from "@/lib/sessions";
import { Sparkles, Music, Baby, Headphones, ArrowRight, Check } from "lucide-react";

const FREE_SESSIONS = new Set(["respiration-5", "present-5", "nuit-20", "sport-5"]);
const FREE_AMBIENTS = new Set(["pink"]);
const FREE_BABY = new Set(["pink"]);

export default function PremiumWelcome() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);

  const unlockedSessions = SESSIONS.filter((s) => !FREE_SESSIONS.has(s.id));
  const unlockedAmbients = AMBIENTS.filter((a) => !FREE_AMBIENTS.has(a.id));
  const unlockedBaby = BABY_AMBIENTS.filter((b) => !FREE_BABY.has(b.id));

  useEffect(() => {
    // Séquence d'entrée : étape 0 → 1 → 2 → 3
    const timers = [
      setTimeout(() => setStep(1), 600),
      setTimeout(() => setStep(2), 1400),
      setTimeout(() => setStep(3), 2200),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  function finish() {
    store.setPremiumWelcomeSeen(true);
    nav("/", { replace: true });
  }

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      data-testid="premium-welcome"
      style={{
        background:
          "radial-gradient(120% 80% at 50% -20%, rgba(136,160,150,0.25) 0%, rgba(11,20,37,0.98) 55%, #080D1A 100%)",
      }}
    >
      {/* Particules de fond */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        {[...Array(14)].map((_, i) => (
          <span
            key={`particle-${i}`}
            className="absolute rounded-full"
            style={{
              width: 6 + (i % 3) * 3,
              height: 6 + (i % 3) * 3,
              left: `${(i * 47) % 100}%`,
              top: `${(i * 73) % 100}%`,
              background: "rgba(178,197,188,0.35)",
              filter: "blur(1px)",
              animation: `pw-float ${5 + (i % 4)}s ease-in-out ${i * 0.2}s infinite`,
              opacity: 0.6,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-md mx-auto px-6 pt-14 pb-24 flex flex-col min-h-screen">
        {/* Halo & sparkle */}
        <div className="mx-auto relative w-32 h-32 flex items-center justify-center animate-fade-in-slow">
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(178,197,188,0.6) 0%, rgba(178,197,188,0) 70%)",
              animation: "pw-pulse 3s ease-in-out infinite",
            }}
          />
          <div className="relative w-20 h-20 rounded-full bg-[color:var(--sauge)]/25 border border-[color:var(--sauge)]/50 flex items-center justify-center backdrop-blur-sm">
            <Sparkles size={32} className="text-[color:var(--sauge-light)]" />
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[color:var(--sauge-light)]">
            Premium activé
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight text-[color:var(--ivoire)] mt-3">
            Bienvenue dans
            <br />
            <span className="italic text-[color:var(--sauge-light)]">votre espace complet.</span>
          </h1>
          <p className="mt-4 text-sm text-[color:var(--ivoire-muted)]">
            Toutes les séances, ambiances et univers bébé sont désormais accessibles.
          </p>
        </div>

        {/* Liste des débloqués — apparaît progressivement */}
        <div className="mt-8 space-y-3">
          <UnlockRow
            visible={step >= 1}
            icon={<Headphones size={18} />}
            label="Séances guidées"
            count={unlockedSessions.length}
            examples={unlockedSessions.slice(0, 3).map((s) => s.title)}
            testid="unlock-sessions"
          />
          <UnlockRow
            visible={step >= 2}
            icon={<Music size={18} />}
            label="Ambiances sonores"
            count={unlockedAmbients.length}
            examples={unlockedAmbients.map((a) => a.label)}
            testid="unlock-ambients"
          />
          <UnlockRow
            visible={step >= 3}
            icon={<Baby size={18} />}
            label="Sons pour bébé"
            count={unlockedBaby.length}
            examples={unlockedBaby.map((b) => b.label)}
            testid="unlock-baby"
          />
        </div>

        <div className="flex-1" />

        <button
          data-testid="premium-welcome-continue"
          onClick={finish}
          className={`mt-10 w-full h-14 rounded-2xl bg-[color:var(--sauge)] text-[color:var(--bg-deep)] font-medium flex items-center justify-center gap-2 transition-all duration-500 ${
            step >= 3 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"
          }`}
        >
          Découvrir mon espace <ArrowRight size={16} />
        </button>
      </div>

      <style>{`
        @keyframes pw-pulse {
          0%, 100% { transform: scale(0.9); opacity: 0.5; }
          50% { transform: scale(1.15); opacity: 1; }
        }
        @keyframes pw-float {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-14px) translateX(6px); }
        }
        .animate-fade-in-slow { animation: fadeInSlow 0.9s ease-out both; }
        @keyframes fadeInSlow {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function UnlockRow({ visible, icon, label, count, examples, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border border-[color:var(--border)] bg-[#0F1B2E]/70 backdrop-blur-sm p-4 flex items-start gap-3 transition-all duration-500 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
    >
      <div className="w-10 h-10 rounded-xl bg-[color:var(--sauge)]/20 border border-[color:var(--sauge)]/40 flex items-center justify-center text-[color:var(--sauge-light)] flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-serif text-lg text-[color:var(--ivoire)]">{label}</h3>
          <span className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)]">
            +{count}
          </span>
        </div>
        <p className="text-xs text-[color:var(--ivoire-muted)] mt-1 line-clamp-2">
          {examples.join(" · ")}
        </p>
      </div>
      <Check size={18} className="text-[color:var(--sauge-light)] flex-shrink-0 mt-1" />
    </div>
  );
}
